/**
 * Click-attribution capture (§12) — captures `gclid` and UTM parameters from
 * the landing URL on first visit, persists them in sessionStorage (survives
 * the sign-up flow within the same tab), and exposes them for the BFF to
 * store on the user profile at account creation and pass into Zoho at commit.
 *
 * Management confirmed: "100% need the gclid data the same as current".
 * Exact Zoho field names pending review with Barbara (§16 #2c).
 * British English project.
 */

const STORAGE_KEY = 'itk-attribution';

export interface ClickAttribution {
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  capturedAt: number; // epoch ms
  landingUrl: string;
}

/** UTM parameter keys to capture. */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/**
 * Capture click attribution from the current URL on first visit.
 * Stores in sessionStorage so it persists through the sign-up flow.
 * Only captures once per session (doesn't overwrite if already present).
 */
export function captureAttribution(): ClickAttribution | null {
  if (typeof window === 'undefined') return null;

  // Don't overwrite if already captured this session.
  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) {
    try {
      return JSON.parse(existing) as ClickAttribution;
    } catch {
      // Corrupted — re-capture.
    }
  }

  const params = new URLSearchParams(window.location.search);
  const gclid = params.get('gclid') ?? undefined;

  // Only capture if there's something to capture.
  if (!gclid && !UTM_KEYS.some((k) => params.has(k))) {
    return null;
  }

  const attribution: ClickAttribution = {
    gclid,
    capturedAt: Date.now(),
    landingUrl: window.location.href,
  };

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) {
      attribution[key] = val;
    }
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  return attribution;
}

/** Get the captured attribution (if any), for sending to the BFF. */
export function getAttribution(): ClickAttribution | null {
  if (typeof window === 'undefined') return null;
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as ClickAttribution;
  } catch {
    return null;
  }
}